import { useState } from "react";
import styled from "styled-components";
import { PromiseLoader } from "react-builder";
import { IQAnswer, IQEndGameResponse, IQQuestion, useIQGame } from "../../providers/IQGame";
import { Attribute, AttributeGroup, AttributeGroupType, AttributeProperty, ComponentProps } from "react-builder";
import { ButtonStateAttributes } from "react-builder";
import { ContainerAttributes } from "react-builder";

export class IQCategory extends Attribute {

    @AttributeProperty({ type: 'String' })
    title: string = 'default';

    @AttributeProperty({ type: 'Color', input: 'color' })
    color: string = '#f00';
}

@AttributeGroup({ title: 'IQGame', blockName: 'IQGame', module: 'iq' })
export class IQGameAttributes extends Attribute {

    @AttributeProperty({ type: 'Image' })
    introThumb?: string;

    @AttributeProperty({ title: 'Categories', arrayLabelProperty: 'title', type: AttributeGroupType.List, arrayType: IQCategory })
    categories: IQCategory[] = [

        new IQCategory().fromJSON({ title: 'Logic', color: '#f38f20' }),
        new IQCategory().fromJSON({ title: 'Math', color: '#00aeef' }),
        new IQCategory().fromJSON({ title: 'Visual', color: '#2bb673' }),
        new IQCategory().fromJSON({ title: 'Focus', color: '#ed1c24' }),
    ]

    @AttributeProperty({ type: 'Object', cssTarget: ' div.container' })
    container: ContainerAttributes = new ContainerAttributes()

    @AttributeProperty({ type: 'Object', cssTarget: ' button.gameButton' })
    button: ButtonStateAttributes = new ButtonStateAttributes()
}

const IQGameTheme = styled.div((props: { attributes: IQGameAttributes, themeColor: string }) => `

    ${props.attributes.toCSS()}

    .game-pause .title h4, .game-progress .type {
        color: ${props.themeColor};
    }

    .game-on{
         .icon {
            width: 80px;
            margin: auto;
        }
    }

    .game-q {
        width: 100%;
        height: 340px;
        margin: 20px 0;
        position: relative;
        border-radius: 6px;
        box-shadow: 0 8px 12px 0 rgb(0 0 0 / 10%), 0 0px 0 1px #f7f9fc;
        background: #fff;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
    }

    .a-inner-wrap {
        overflow: hidden;
        position: relative;
        flex: 1;
        margin-right: 10px;
        user-select: none;
        box-shadow: inset 0 0 0 1px #c9ddf0;
        border-radius: 6px;
        transition: .2s ease;
        cursor: pointer;
    }

    .a-inner-wrap .num {
        background: ${props.themeColor};
        position: absolute;
        top: 0;
        left: 5px;
        width: 20px;
        height: 20px;
        border-radius: 0 0 4px 4px;
        text-align: center;
        line-height: 1.1;
        font-weight: 600;
        color: #fff;
    }

    .a-inner-wrap:hover {
        box-shadow: inset 0 0 0 1px ${props.themeColor}, inset 0 -3px 0 ${props.themeColor};
    }
    .a-inner-wrap:hover {
        border-radius: 10px;
    }

    .game-a {
        height: 120px;
    }

    .inner-progress{
        background: #c9ddf0;
        height: 10px;

        .memory {
            background: ${props.themeColor};
            height: 100%;
        }
    }

    .d-flex{
        display: flex;
    } 
    
    .flex-row{
        flex-direction: row;
    }

`);

export enum IQGameStatus {
    Over,
    Init,
    InProgress
}

export interface GameState {
    status: IQGameStatus
    nextQuestion?: IQQuestion,
    questionIndex: number
    categoryIndex: number
    questionCount: number
    categoryQuestionCount: number
    result?: IQEndGameResponse
}

function IQGame(props: ComponentProps<IQGameAttributes>) {

    const iqGame = useIQGame();
    const [gameState, setGameState] = useState<GameState>({ status: IQGameStatus.Init, categoryIndex: 0, questionCount: 0, questionIndex: 0, categoryQuestionCount: 0 });
    const [gameStartPromise, setGameStartPromise] = useState<Promise<any>>();
    const [timer, setTimer] = useState<number>(0);



    // const categories = ["Logic", "Math", "Visual", "Focus"];
    const answerLetters = ["A", "B", "C", "D"];

    const startGame = () => {
        setGameStartPromise(Promise.all([iqGame?.startGame(), iqGame?.getQuestionsPerCategory()]).then((q) => {

            iqGame?.startCategory().then((r) => {

                setGameState((s) => ({ ...s, status: IQGameStatus.InProgress, nextQuestion: q[0], questionCount: q[1] ?? 0 }))
                setInterval(() => {
                    setTimer((timer) => timer + 1);
                }, 1000);
            })
            return q[0];
        }))
    }

    const startCategory = () => {
        iqGame?.startCategory();
        setGameState((s) => ({ ...s, status: IQGameStatus.InProgress }))
    }

    const answerQuestion = (a: IQAnswer) => {
        setGameStartPromise(iqGame?.answerQuestion(a).then((q) => {

            // change category
            if (gameState.questionIndex + 1 >= gameState.questionCount * props.attributes.categories.length) {
                // end game
                setGameStartPromise(iqGame?.endCategory().then((r) => {
                    return iqGame?.endGame().then((r) => {
                        console.log(r);
                        setGameState((s) => ({ ...s, status: IQGameStatus.Over, result: r }));
                        return r;
                    });
                }));
            } else if (gameState.categoryQuestionCount + 1 >= gameState.questionCount) {
                // end category 
                console.log('end category');
                iqGame?.endCategory();
                setGameState((s) => ({ ...s, status: IQGameStatus.Init, nextQuestion: q, categoryIndex: s.categoryIndex + 1, questionIndex: s.questionIndex + 1, categoryQuestionCount: 0, }))
            } else {

                setGameState((s) => ({ ...s, status: IQGameStatus.InProgress, nextQuestion: q, questionIndex: s.questionIndex + 1, categoryQuestionCount: s.categoryQuestionCount + 1 }))
            }
            return q;
        }))
    }

    const buildTime = (totalSeconds: number) => {
        const totalMs = totalSeconds * 1000;
        const result = new Date(totalMs).toISOString().slice(11, 19);
        return result;
    }

    const buildHeader = () => <div className="game-progress">
        <div className="current d-flex flex-row justify-content-between">
            <div className="type ng-binding" ng-style="categoryStyle" style={{ color: props.attributes.categories[gameState.categoryIndex].color }}>{props.attributes.categories[gameState.categoryIndex].title}</div>
            <div className="ng-isolate-scope timer"><span className="ng-binding ng-scope">{buildTime(timer)}</span></div>
            <div className="stopwatch">
                <div className="question ng-binding">
                    {gameState.categoryQuestionCount + 1}/{gameState.questionCount}
                </div>
            </div>
        </div>
        <div className="inner-progress w-100 d-flex"><div className="memory" style={{ width: `${gameState.categoryQuestionCount / gameState.questionCount * 100}%` }}></div></div>
    </div>

    const buildQuestionPage = (quetion: IQQuestion) =>
        <div className="game-wrap">
            {buildHeader()}
            <div className="game-q" style={{ backgroundImage: `url('http://d1gdbea65l8l3c.cloudfront.net${quetion.qImg}')` }}></div>
            <div className="game-title ng-binding">
                {quetion.text}
            </div>
            <div className="game-a-wrap d-flex flex-row">
                {quetion.answers.map((a, i) =>
                    <div key={i} className="a-inner-wrap" onClick={(e) => answerQuestion({ answerId: a.answerId, answerNumber: a.answerNum, questionId: gameState.nextQuestion!.id, questionNum: gameState.questionIndex })}>
                        <div className="num">{answerLetters[i]}</div>
                        <div className="game-a" ng-click="updateSelection(1)">
                            <img className="a-img 1" src={`http://d1gdbea65l8l3c.cloudfront.net${a.aImg}`} />
                        </div>
                    </div>
                )}
            </div>
        </div>

    const buildInitPage = () => <div className="game-pause" >
        <div id="pause" className="game-pause-wrap">
            <div className="game-on" >
                <figure className="icon">
                    {props.attributes.introThumb && <img src={props.attributes.introThumb} />}
                </figure>
                <div className="title">
                    <h3 className="ng-binding">STAGE {"I".repeat(gameState.categoryIndex + 1)}</h3>
                    <h4 className="ng-binding">{props.attributes.categories[gameState.categoryIndex].title}</h4>
                </div>
                <button type="button" className="gameButton button ng-binding" onClick={(e) => gameState.categoryIndex == 0 ? startGame() : startCategory()} >
                    START
                </button>
            </div>
            {/* <div className="game-pause ng-hide" ng-show="!gameOn">
                <div className="title">
                <h3>Good Job!</h3>
                <h4 id="resultLoader">Please Wait...</h4>
                </div>
                <div className="calc"></div>
            </div> */}
        </div>
    </div>

    const buildError = () => {
        return <h3>Unexpected error</h3>
    }

    const buidResultsPage = (results?: IQEndGameResponse) => {
        return (
            results ? <div id="score" className="score-wrap">
                <div className="close-icon" ng-click="$ctrl.cancel()"></div>
                <div className="content score">
                    <h3 className="title">
                        Results & Stats
                    </h3>
                    <div className="scores">
                        <div className="time d-flex flex-row justify-content-between">
                            <span>Time</span>
                            <span className="ng-binding">
                                {buildTime(results.hours * 60 * 60 + results.minutes * 60 + results.seconds + results.millis / 1000)}
                            </span>
                        </div>
                        <div className="d-flex flex-row justify-content-between" style={{ color: props.attributes.categories[0].color }}>
                            <div className="memory">{props.attributes.categories[0].title}</div>
                            <div className="memory ng-binding">{results.firstCategoryScore}/{gameState.categoryQuestionCount}</div>
                        </div>
                        <div className="d-flex flex-row justify-content-between" style={{ color: props.attributes.categories[1].color }}>
                            <div className="memory">{props.attributes.categories[1].title}</div>
                            <div className="memory ng-binding">{results.secondCategoryScore}/{gameState.categoryQuestionCount}</div>
                        </div>
                        <div className="d-flex flex-row justify-content-between" style={{ color: props.attributes.categories[2].color }}>
                            <div className="memory">{props.attributes.categories[2].title}</div>
                            <div className="memory ng-binding">{results.thirdCategoryScore}/{gameState.categoryQuestionCount}</div>
                        </div>
                        <div className="d-flex flex-row justify-content-between" style={{ color: props.attributes.categories[3].color }}>
                            <div className="memory">{props.attributes.categories[3].title}</div>
                            <div className="memory ng-binding">{results.fourthCategoryScore}/{gameState.categoryQuestionCount}</div>
                        </div>
                        <div className="rank d-flex flex-row justify-content-between">
                            <span>Rank</span>
                            <span className="ng-binding">{results.rank}</span>
                        </div>
                        <a href="#results" >
                            <button className="scorebtn" >Review Statistics</button>
                        </a>
                    </div>
                </div>
            </div> : buildError()

        );
    }

    const buildGame = () => {
        switch (gameState.status) {
            case IQGameStatus.Over:
                return buidResultsPage(gameState.result);
            case IQGameStatus.InProgress:
                return gameState.nextQuestion ? buildQuestionPage(gameState.nextQuestion!) : buildError();
            case IQGameStatus.Init:
            default:
                return buildInitPage();
        }
    }

    const getThemeColor = () => {
        return props.attributes?.categories[gameState.categoryIndex]?.color;
    }

    return (
        <IQGameTheme {...props} themeColor={getThemeColor()}>
            <div className="container" >
                <PromiseLoader promise={gameStartPromise} loader={<>Loading...</>} >
                    {res => buildGame()}
                </PromiseLoader>
            </div>
        </IQGameTheme>
    )
}


IQGame.prototype.Props = IQGameAttributes;


export default IQGame;